import { useQuery } from '@tanstack/react-query';
import { i18n } from 'i18next';
import numbro from 'numbro';
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { ToggleButtonGroup } from 'react-bootstrap';
import Badge from 'react-bootstrap/Badge';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import ToggleButton from 'react-bootstrap/ToggleButton';
import { useTranslation } from 'react-i18next';
import { FaEllipsisH, FaHeart, FaThumbsDown, FaThumbsUp } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { miscActions } from '../../actions';
import Binance from '../../assets/exchange/binance.svg';
import Bybit from '../../assets/exchange/bybit.svg';
import LoadingImage from '../../components/general/LoadingImage';
import LikeButton from '../../components/like/LikeButton';
import LikeOverlay from '../../components/like/LikeOverlay';
import DLTable from '../../components/table/DLTable/DLTable';
import {
  generateRadarChartData,
  RANKS,
} from '../../components/visualize/WinPercentageCardUtils';
import { uiHelpers } from '../../helpers';
import useDispatch from '../../hooks/useDispatch';
import { useUser } from '../../hooks/users';
import useSelector from '../../hooks/useSelector';
import {
  FavEva,
  FavStockWithCurrentPrice,
} from '../../py-generated/datalouder/favorite_stock/types';
import { Category } from '../../py-generated/datalouder/stock/types';
import { miscService } from '../../services';
import { Region } from '../../shared/types';

const COLUMN_CHANGE = 'fav-stock-change-%';
type Section = 'HK' | 'US' | 'CRPT';
const ALL_SECTIONS: Section[] = ['HK', 'US', 'CRPT'];
const categoryToSection = (category: Category): Section => {
  switch (category) {
    case 'CRPT':
    case 'CRPTBN': {
      return 'CRPT';
    }
    default: {
      return category;
    }
  }
};
const sectionToRegion = (section: Section): Region => {
  switch (section) {
    case 'HK': {
      return 'hk';
    }
    case 'CRPT':
    case 'US': {
      return 'us';
    }
  }
};

const WinPercentageCardLazy = React.lazy(
  () => import('../../components/visualize/WinPercentageCard')
);

const changePercentCell = ({ cell: { value } }) => (
  <div style={{ color: uiHelpers.colorForChange(value) }}>
    {value === null ? '-' : numbro(value).format('+0.00%')}
  </div>
);

const dateCell =
  (i18n: i18n) =>
  ({ cell: { value } }) => {
    const date = new Date(value * 1000);
    return (
      <span>
        <span>{uiHelpers.formatDateLocal(date)}</span>
        <br />
        <span>
          <Badge bg="secondary">
            {uiHelpers.formatDeltaDateTime(i18n, date)}
          </Badge>
        </span>
      </span>
    );
  };

const CHANGE_PERCENT_CELL_CLASS =
  'change-%-cell fav-stock-floating-modal-general-cell';

export const StyledModalHeader = styled(Modal.Header)`
  border-bottom: 0 none;
  padding: 0;
  margin-top: 0;
  margin-bottom: 0.5rem;
`;

const EvaluationContainer = styled.div`
  .react-table .td {
    font-size: 1em !important;
  }
`;

const percentChange = (before, after) => {
  if (before === 0) return 1;
  return (after - before) / before;
};

const useColumns = () => {
  const { t, i18n } = useTranslation();

  const renderStockCell = ({ cell: { value } }) => {
    return (
      <LikeOverlay stockCode={value.ticker}>
        <div
          style={{
            width: '100%',
            textAlign: 'left',
            minHeight: 48,
            padding: '5px 0',
          }}
        >
          <div style={{ fontWeight: 'bold', fontSize: 14 }}>
            {value.category === 'CRPTBN' ? <img src={Binance} /> : ''}
            {value.category === 'CRPT' ? <img src={Bybit} /> : ''}{' '}
            {value.ticker}
          </div>
          <div style={{ color: '#888888' }}>{value.name[i18n.language]} </div>
        </div>
      </LikeOverlay>
    );
  };

  const columns = [
    {
      Header: t('Stock Code'),
      id: 'fav-stock-stockCode',
      width: 120,
      disableFilters: true,
      accessor: (item: FavStockWithCurrentPrice) => item.stock,
      Cell: renderStockCell,
      className: 'stock-code-cell align-left',
    },
    {
      Header: t('Stock Price'),
      id: 'fav-stock-stockPrice',
      width: 80,
      disableFilters: true,
      accessor: (item: FavStockWithCurrentPrice) => item.current_price,
      Cell: ({ cell: { value } }) => {
        return <div>{uiHelpers.formatMoney(value)}</div>;
      },
      className: 'fav-stock-floating-modal-general-cell',
    },
    {
      Header: t('Added At'),
      id: 'fav-stock-added-at',
      width: 80,
      disableFilters: true,
      accessor: (item: FavStockWithCurrentPrice) => item.stock.ts,
      Cell: dateCell(i18n),
      className: 'fav-stock-floating-modal-general-cell',
    },
    {
      Header: t('Change %'),
      id: COLUMN_CHANGE,
      width: 80,
      disableFilters: true,
      accessor: (item: FavStockWithCurrentPrice) => {
        return item.current_price && item.stock.price
          ? percentChange(item.stock.price, item.current_price)
          : null;
      },
      Cell: changePercentCell,
      className: CHANGE_PERCENT_CELL_CLASS,
    },
    {
      Header: () => <FaEllipsisH />,
      id: 'fav-stock-like/dislike',
      width: 48,
      disableFilters: true,
      accessor: (item: FavStockWithCurrentPrice) => item.stock.unlike,
      Cell: ({
        row: {
          original: {
            stock: { ticker, category },
          },
        },
      }) => {
        return (
          <div>
            <LikeButton category={category} ticker={ticker} showBothButtons />
          </div>
        );
      },
      className: 'like-dislike-cell fav-stock-floating-modal-general-cell',
    },
  ];

  const deletedStocksColumns = [
    {
      Header: t('Stock Code'),
      id: 'fav-stock-stockCode',
      width: 100,
      disableFilters: true,
      accessor: item => item,
      Cell: renderStockCell,
      className: 'stock-code-cell align-left',
    },
    {
      Header: t('Added Price'),
      id: 'fav-stock-createdPrice',
      width: 70,
      disableFilters: true,
      accessor: (item: FavEva) => item.open_price,
      Cell: ({ cell: { value } }) => {
        return <div>{uiHelpers.formatMoney(value)}</div>;
      },
      className: 'stock-price-cell fav-stock-floating-modal-general-cell',
    },
    {
      Header: t('Added At'),
      id: 'fav-stock-createdAt',
      width: 90,
      disableFilters: true,
      accessor: (item: FavEva) => item.open_time,
      Cell: dateCell(i18n),
      className: 'updated-at-cell fav-stock-floating-modal-general-cell',
    },
    {
      Header: t('Removed Price'),
      id: 'fav-stock-deletedPrice',
      width: 70,
      disableFilters: true,
      accessor: (item: FavEva) => item.close_price,
      Cell: ({ cell: { value } }) => {
        return <div>{uiHelpers.formatMoney(value)}</div>;
      },
      className: 'stock-price-cell fav-stock-floating-modal-general-cell',
    },
    {
      Header: t('remove At'),
      id: 'fav-stock-deletedAt',
      width: 90,
      disableFilters: true,
      accessor: (item: FavEva) => item.close_time,
      Cell: dateCell(i18n),
      className: 'updated-at-cell fav-stock-floating-modal-general-cell',
    },
    {
      Header: t('Change %'),
      id: COLUMN_CHANGE,
      width: 80,
      disableFilters: true,
      accessor: (item: FavEva) => {
        return item.open_price && item.close_price
          ? percentChange(item.open_price, item.close_price)
          : null;
      },
      Cell: changePercentCell,
      className: CHANGE_PERCENT_CELL_CLASS,
    },
    {
      Header: () => <FaEllipsisH />,
      id: 'fav-stock-like/dislike-status',
      width: 28,
      disableFilters: true,
      accessor: (item: FavEva) => item.unlike,
      Cell: ({ cell: { value } }) => {
        return <div>{value ? <FaThumbsDown /> : <FaThumbsUp />}</div>;
      },
      className: 'like-dislike-cell fav-stock-floating-modal-general-cell',
    },
  ];
  return { columns, deletedStocksColumns };
};

const FavoriteStockFloatingButton: React.FC = () => {
  const [show, setShow] = useState(false);

  const { t } = useTranslation();

  const user = useUser();
  const nagivate = useNavigate();

  const handleToggle = useCallback(() => {
    if (user) {
      setShow(value => !value);
    } else {
      nagivate('/register');
    }
  }, [nagivate, user]);

  return (
    <>
      <div
        style={{ paddingTop: '0.625rem' }}
        onClick={handleToggle}
        className="clickable favorite-stock-float-button"
        id="favorite-stock-float-button"
      >
        <div style={{ display: 'inline-block' }}>
          <FaHeart style={{ height: '0.75rem', width: '0.75rem' }} />
        </div>{' '}
        <div style={{ display: 'inline-block', fontSize: '0.8125rem' }}>
          {t('fav-list')}
        </div>
      </div>

      <Modal
        show={show}
        centered
        onHide={() => setShow(false)}
        size="xl"
        scrollable
      >
        <Modal.Body>{show && <FavoriteStockModalContent />}</Modal.Body>
      </Modal>
    </>
  );
};

const FavoriteStockModalContent: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { data: favStockDetails, isLoading: loading } = useQuery(
    ['listFavoriteStockDetails'],
    () => miscService.listFavoriteStockDetails()
  );

  const [section, setSection] = useState<Section>('HK');

  useEffect(() => {
    dispatch(miscActions.loadUserWinRates(sectionToRegion(section)));
  }, [dispatch, section]);

  const { myRanks } = useSelector(state => state.misc.loadUserWinRates);

  const { columns, deletedStocksColumns } = useColumns();

  const commonTableProps = {
    loading,
    defaultSortBy: [
      {
        id: 'fav-stock-change-%',
        desc: true,
      },
    ],
    defaultPageSize: 20,
  };

  const winPercentageData = myRanks?.myPercentageRanks
    ? myRanks.myPercentageRanks.map(item => RANKS.indexOf(item))
    : [];
  const winRateData = myRanks?.myRateRanks
    ? myRanks.myRateRanks.map(item => RANKS.indexOf(item))
    : [];

  const data = generateRadarChartData(t, winRateData, winPercentageData);

  return (
    <div>
      <StyledModalHeader closeButton>
        <Modal.Title style={{ fontSize: 18, padding: 0, marginTop: 0 }}>
          {t(`win-analyse-${section}`)}
          <LoadingImage loading={loading} />{' '}
          <ToggleButtonGroup type="radio" name="options" defaultValue={'HK'}>
            {ALL_SECTIONS.map((r, idx) => (
              <ToggleButton
                key={r}
                variant="outline-secondary"
                name="radio"
                value={r}
                checked={section === r}
                onClick={() => setSection(r)}
                id={idx.toString()}
              >
                {r}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Modal.Title>
      </StyledModalHeader>
      <Suspense fallback={<LoadingImage loading displayAsBlock />}>
        <WinPercentageCardLazy
          t={t}
          ranks={myRanks}
          data={data}
          note={`* ${t('win-percentage-note')}`}
          winPercentageKey="myPercentagesRaw"
          winRateKey="myRatesRaw"
        />
      </Suspense>
      <Row>
        <Col md={6}>
          <StyledModalHeader className="border-0">
            <Modal.Title style={{ fontSize: 18, padding: 0, marginTop: 0 }}>
              {t('fav-stock')}
              <LoadingImage loading={loading} />
            </Modal.Title>
          </StyledModalHeader>
          <DLTable
            tableId="fav-stock"
            columns={columns}
            data={
              // only display stocks from current region
              (favStockDetails?.stocks || []).filter(
                stock => categoryToSection(stock.stock.category) === section
              )
            }
            {...commonTableProps}
          />
        </Col>
        <Col md={6}>
          <StyledModalHeader>
            <Modal.Title style={{ fontSize: 18, padding: 0, marginTop: 0 }}>
              {t('fav-history')}
              <LoadingImage loading={loading} />
            </Modal.Title>
          </StyledModalHeader>
          {(favStockDetails?.evaluations || []).some(
            evaluation => categoryToSection(evaluation.category) === section
          ) ? (
            <EvaluationContainer>
              <DLTable
                tableId="fav-stock-deleted"
                columns={deletedStocksColumns}
                data={
                  // only display stocks from current region
                  (favStockDetails?.evaluations || []).filter(
                    evaluation =>
                      categoryToSection(evaluation.category) === section
                  )
                }
                {...commonTableProps}
              />
            </EvaluationContainer>
          ) : (
            <div>{t('no-fav-history')}</div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default FavoriteStockFloatingButton;
